import { Button, Form, Row, Col, InputGroup } from 'react-bootstrap';

// Componente para o botão "Adicionar n linhas"
interface AdicionarMultiplasLinhasProps {
  quantidade: number;
  onDecrementar: () => void;
  onIncrementar: () => void;
  onQuantidadeChange: (value: number) => void;
  onAdicionar: () => void;
}

export const AdicionarMultiplasLinhas = ({
  quantidade,
  onDecrementar,
  onIncrementar,
  onQuantidadeChange,
  onAdicionar,
}: AdicionarMultiplasLinhasProps) => {
  return (
    <div className="d-flex align-items-center gap-2">
      <span className="text-muted">Adicionar</span>
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={onDecrementar}
        style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
      >
        <i className="bi bi-dash">-</i>
      </Button>
      <Form.Control
        type="number"
        min="1"
        max="50"
        value={quantidade}
        onChange={(e) => onQuantidadeChange(Math.max(1, parseInt(e.target.value) || 1))}
        style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
        size="sm"
      />
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={onIncrementar}
        style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
      >
        <i className="bi bi-plus">+</i>
      </Button>
      <Button
        variant="outline-primary"
        size="sm"
        onClick={onAdicionar}
        style={{ borderRadius: '8px' }}
      >
        <i className="bi bi-plus-lg me-1"></i>
        Adicionar {quantidade} linha{quantidade > 1 ? 's' : ''}
      </Button>
    </div>
  );
};

// Componente para input de processo com botão adicionar
interface ProcessoInputProps {
  value: string;
  onChange: (value: string) => void;
  onAdicionar: () => void;
  placeholder?: string;
  buttonVariant?: string;
  buttonText?: string;
}

export const ProcessoInput = ({
  value,
  onChange,
  onAdicionar,
  placeholder = 'Ex: 000000.000.000',
  buttonVariant = 'primary',
  buttonText = 'Adicionar',
}: ProcessoInputProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorApenasNumeros = apenasNumeros(e.target.value);
    onChange(valorApenasNumeros);
  };

  return (
    <InputGroup>
      <Form.Control
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onAdicionar();
          }
          // Permitir apenas números
          if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
            e.preventDefault();
          }
        }}
        style={{ borderRadius: '8px 0 0 8px' }}
      />
      <Button
        variant={buttonVariant as any}
        onClick={onAdicionar}
        style={{ borderRadius: '0 8px 8px 0' }}
      >
        <i className="bi bi-plus-lg me-2"></i>
        {buttonText}
      </Button>
    </InputGroup>
  );
};

// Componente para estado vazio (empty state)
interface EmptyStateProps {
  icon?: string;
  message: string;
  subMessage?: string;
}

export const EmptyState = ({ icon = 'bi-inbox', message, subMessage }: EmptyStateProps) => {
  return (
    <div className="text-center text-muted py-4 border rounded">
      <i className={`bi ${icon} fs-1`}></i>
      <p className="mb-0 mt-2">{message}</p>
      {subMessage && <small>{subMessage}</small>}
    </div>
  );
};

// Componente para contador total
interface TotalCounterProps {
  label: string;
  count: number;
}

export const TotalCounter = ({ label, count }: TotalCounterProps) => {
  return (
    <h5 className="mb-0 text-primary">
      <small className="text-muted">{label}:</small> {count}
    </h5>
  );
};

// Componente para linha de controles (adicionar múltiplas linhas + contador)
interface ControlesLinhaProps {
  quantidade: number;
  onDecrementar: () => void;
  onIncrementar: () => void;
  onQuantidadeChange: (value: number) => void;
  onAdicionar: () => void;
  totalLabel: string;
  totalCount: number;
}

export const ControlesLinha = ({
  quantidade,
  onDecrementar,
  onIncrementar,
  onQuantidadeChange,
  onAdicionar,
  totalLabel,
  totalCount,
}: ControlesLinhaProps) => {
  return (
    <Row className="mb-4">
      <Col md={6}>
        <AdicionarMultiplasLinhas
          quantidade={quantidade}
          onDecrementar={onDecrementar}
          onIncrementar={onIncrementar}
          onQuantidadeChange={onQuantidadeChange}
          onAdicionar={onAdicionar}
        />
      </Col>
      <Col md={6} className="d-flex align-items-center justify-content-end">
        <TotalCounter label={totalLabel} count={totalCount} />
      </Col>
    </Row>
  );
};

// Função helper para formatar data
export const formatarData = (data: Date | string): string => {
  return new Date(data).toLocaleDateString('pt-BR');
};

// Função helper para aceitar apenas números
export const apenasNumeros = (valor: string): string => {
  return valor.replace(/\D/g, '');
};

// Função helper para aceitar apenas letras e espaços
export const apenasLetras = (valor: string): string => {
  return valor.replace(/[^a-zA-ZÀ-ÿ\s]/g, '');
};
